import React, { useEffect, useState, useRef } from 'react';
import { getPicks, getTournaments, getPools, getPoolEntries } from '../services/api';

const ROUND_COLORS = {
    1: 'border-gray-700',
    2: 'border-blue-800',
    3: 'border-purple-800',
    4: 'border-yellow-800',
    5: 'border-orange-700',
    6: 'border-orange-500',
};

const ROUND_LABELS = {
    1: 'First Round',
    2: 'Second Round',
    3: 'Sweet 16',
    4: 'Elite 8',
    5: 'Final Four',
    6: 'Championship',
};

function PickCard({ pick }) {
    const isHidden  = pick.hidden_until_deadline === true;
    const isPending = pick.is_correct === null;
    const isCorrect = pick.is_correct === true;

    let borderColor = 'border-gray-700';
    let badgeColor  = 'bg-gray-700 text-gray-400';
    let badgeLabel  = 'Pending';

    if (isHidden) {
        // Pre-deadline pick belonging to another participant. Backend has
        // nulled the prediction fields and set hidden_until_deadline:true.
        // Render a neutral "Hidden" badge and suppress the "Pick:" line so
        // the user understands a pick exists but its content isn't visible.
        borderColor = 'border-gray-800';
        badgeColor  = 'bg-gray-800 text-gray-500';
        badgeLabel  = 'Hidden until deadline';
    } else if (!isPending && isCorrect) {
        borderColor = 'border-green-600';
        badgeColor  = 'bg-green-900 text-green-400';
        badgeLabel  = 'Correct';
    } else if (!isPending && !isCorrect) {
        borderColor = 'border-red-700';
        badgeColor  = 'bg-red-900 text-red-400';
        badgeLabel  = 'Wrong';
    }

    return (
        <div className={'bg-gray-900 rounded-lg px-4 py-3 border ' + borderColor}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' + badgeColor}>
                        {badgeLabel}
                    </span>
                    <span className="text-gray-400 text-xs">
                        {pick.team1} vs {pick.team2}
                    </span>
                </div>
                {!isHidden && (
                    <span className={'text-sm font-black ' + (pick.points_earned > 0 ? 'text-[var(--org-accent,#fb923c)]' : 'text-gray-600')}>
                        +{pick.points_earned} pts
                    </span>
                )}
            </div>
            {!isHidden && (
                <div className="mt-2 flex items-center gap-2">
                    <span className="text-white font-bold text-sm">
                        Pick: {pick.picked_team}
                    </span>
                    {!isPending && (
                        <span className="text-gray-500 text-xs">
                            Winner: {pick.winning_team}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function BracketView({ user, initialTarget = null, onTargetConsumed }) {
    const [tournaments,      setTournaments]      = useState([]);
    const [activeTournament, setActiveTournament] = useState(null);
    const [pools,            setPools]            = useState([]);
    const [activePool,       setActivePool]       = useState(null);
    const [entries,          setEntries]          = useState([]);
    const [activeEntry,      setActiveEntry]      = useState(null);
    const [picks,            setPicks]            = useState([]);
    const [loading,          setLoading]          = useState(false);
    const [error,            setError]            = useState('');

    // Guards against out-of-order async responses: if the user (or a test)
    // switches tournament/pool quickly, an EARLIER request can resolve
    // AFTER a later one and overwrite fresh state with stale data -- e.g.
    // getPools(oldTournamentId) resolving after getPools(newTournamentId)
    // would silently replace the correct pool buttons with the wrong
    // (or empty) ones. Each ref tracks "what is the current request for,"
    // and the corresponding .then() bails out if it's no longer current
    // by the time it resolves, rather than trusting resolution order.
    const tournamentsRequestId = useRef(0);
    const poolsRequestId       = useRef(0);
    const entriesRequestId     = useRef(0);

    useEffect(() => {
        const requestId = ++tournamentsRequestId.current;
        getTournaments()
            .then(res => {
                if (requestId !== tournamentsRequestId.current) return; // superseded
                setTournaments(res.data);
                if (res.data.length > 0) {
                    const target = initialTarget?.tournamentId
                        ? res.data.find(t => t.id === initialTarget.tournamentId) || res.data[0]
                        : res.data[0];
                    setActiveTournament(target);
                }
            })
            .catch(() => setError('Failed to load tournaments'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!activeTournament) return;
        setActivePool(null);
        setPools([]);
        setEntries([]);
        setActiveEntry(null);
        setPicks([]);
        const requestId = ++poolsRequestId.current;
        getPools(activeTournament.id)
            .then(res => {
                if (requestId !== poolsRequestId.current) return; // superseded
                setPools(res.data);
                if (res.data.length > 0) {
                    const target = initialTarget?.poolId
                        ? res.data.find(p => p.id === initialTarget.poolId) || res.data[0]
                        : res.data[0];
                    setActivePool(target);
                }
            })
            .catch(() => setError('Failed to load pools'));
    }, [activeTournament]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!activePool) return;
        setActiveEntry(null);
        setPicks([]);
        const requestId = ++entriesRequestId.current;
        getPoolEntries(activePool.id)
            .then(res => {
                if (requestId !== entriesRequestId.current) return; // superseded
                setEntries(res.data);
                if (initialTarget?.entryId) {
                    const target = res.data.find(e => e.entry_id === initialTarget.entryId);
                    if (target) {
                        loadPicks(target);
                    }
                    if (onTargetConsumed) onTargetConsumed();
                }
            })
            .catch(() => setError('Failed to load participants'));
    }, [activePool]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPicks = (entry) => {
        setLoading(true);
        setActiveEntry(entry);
        getPicks(entry.entry_id)
            .then(res => setPicks(res.data))
            .catch(() => setError('Failed to load picks'))
            .finally(() => setLoading(false));
    };

    const picksByRound = picks.reduce((acc, pick) => {
        const round = pick.round_number;
        if (!acc[round]) acc[round] = [];
        acc[round].push(pick);
        return acc;
    }, {});

    const totalPoints  = picks.reduce((sum, p) => sum + (p.points_earned || 0), 0);
    const correctCount = picks.filter(p => p.is_correct === true).length;
    const wrongCount   = picks.filter(p => p.is_correct === false).length;
    const pendingCount = picks.filter(p => p.is_correct === null).length;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-wide">
                Bracket View
            </h2>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <select
                    value={activeTournament?.id || ''}
                    onChange={e => {
                        setActiveTournament(tournaments.find(t => t.id === parseInt(e.target.value)));
                    }}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[var(--org-accent,#f97316)]">
                    {tournaments.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            </div>

            <div className="flex gap-2 mb-6">
                {pools.map(pool => (
                    <button
                        key={pool.id}
                        onClick={() => setActivePool(pool)}
                        className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                            (activePool && activePool.id === pool.id
                                ? 'bg-[var(--org-accent,#f97316)] text-white'
                                : 'bg-gray-800 text-gray-400 hover:text-white')}
                    >
                        {pool.name}
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
                {entries.map(entry => (
                    <button
                        key={entry.entry_id}
                        onClick={() => loadPicks(entry)}
                        className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                            (activeEntry && activeEntry.entry_id === entry.entry_id
                                ? 'bg-[var(--org-accent,#f97316)] text-white'
                                : 'bg-gray-800 text-gray-400 hover:text-white')}
                    >
                        {entry.participant}
                    </button>
                ))}
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                    {error}
                </div>
            )}

            {picks.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4 mb-6 flex items-center justify-between">
                    <div className="flex gap-6">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Correct</p>
                            <p className="text-xl font-black text-green-400">{correctCount}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Wrong</p>
                            <p className="text-xl font-black text-red-400">{wrongCount}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
                            <p className="text-xl font-black text-gray-400">{pendingCount}</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                        <p className="text-3xl font-black text-[var(--org-accent,#fb923c)]">{totalPoints} pts</p>
                    </div>
                </div>
            )}

            {loading && (
                <div className="text-gray-400 text-center py-12">Loading picks...</div>
            )}

            {!loading && picks.length === 0 && activeEntry && (
                <div className="text-gray-500 text-center py-12">No picks found for this entry.</div>
            )}

            {!loading && picks.length === 0 && !activeEntry && (
                <div className="text-gray-500 text-center py-12">
                    {entries.length === 0
                        ? 'No participants are enrolled yet. Approve registrations from the Participants tab to get started.'
                        : 'Select a participant above to view their bracket.'
                    }
                </div>
            )}

            {!loading && picks.length > 0 && (
                Object.keys(picksByRound).sort((a, b) => a - b).map(round => (
                    <div key={round} className="mb-8">
                        <div className={'border-l-4 ' + ROUND_COLORS[round] + ' pl-3 mb-3'}>
                            <h3 className="text-sm font-black text-gray-300 uppercase tracking-wider">
                                {ROUND_LABELS[round]}
                            </h3>
                            <p className="text-xs text-gray-500">
                                {picksByRound[round].filter(p => p.is_correct).length} of {picksByRound[round].length} correct
                            </p>
                        </div>
                        <div className="space-y-2">
                            {picksByRound[round].map(pick => (
                                <PickCard key={pick.pick_id} pick={pick} />
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

export default BracketView;